import PageLayout from '@/components/shared/PageLayout';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft, Save, X } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useAuth } from '@/contexts/AuthContext';
import { getClient, updateClient } from '@/components/clients/services/clients-api';
import React, { useState, useEffect } from "react";
import type { Client } from "@/lib/types";
import { Skeleton } from "@/components/ui/skeleton";
import { ActionToolbar } from '@/components/shared/ActionToolbar';
import { ToolbarButton } from "@/components/shared/ToolbarButton";
import { DatePickerField } from "@/components/ui/date-picker-field";
import { formatPhoneInternational, safeToDate } from "@/lib/utils";
import { useSettings } from "@/components/settings/lib/settings-context";
import { convertHeightToMetric, convertWeightToMetric } from "./lib/unit-conversion";
import { HeightDisplay, WeightDisplay } from "@/components/ui/unit-display";
import {
  PAIN_SCALE_NRS10,
  normalizePainLevelNrs,
} from "@/lib/clients/roster-fields";
import { PainLevelNrsSliderField } from "@/components/clients/fields/PainLevelNrsSliderField";

const SEX_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'] as const;

const formSchema = z.object({
  name: z.string().min(1, { message: "Full name is required." }),
  email: z.preprocess(
    (val: unknown) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    z.string().email({ message: "Invalid email address." }).optional()
  ),
  height: z.number().positive({ message: "Height must be a positive number." }),
  dateOfBirth: z.date().optional(),
  sex: z.enum(SEX_OPTIONS).optional(),
  address: z.string().optional(),
  phone: z.string().optional(),
  weight: z.number().positive({ message: "Weight must be a positive number." }).optional(),
  classification: z.string().optional(),
  medicalHistory: z.string().optional(),
  injuryHistory: z.string().optional(),
  goals: z.string().optional(),
  liftingExperience: z.number().min(0).max(3).optional(),
  painLevel: z.number().min(0).max(10).optional(),
});
import logger from '@/lib/logging';

const experienceLevels = ["Beginner Lifter", "Novice Lifter", "Intermediate Lifter", "Athlete"];

export default function ClientEditPage() {
  const { id: clientId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const { settings } = useSettings();
  const { unit } = settings;
  const { user, loading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema) as any,
    defaultValues: {
      name: "",
      address: "",
      phone: "",
      email: "",
      sex: undefined,
      height: 0,
      weight: undefined,
      classification: "",
      medicalHistory: "",
      injuryHistory: "",
      goals: "",
      liftingExperience: 0,
      painLevel: undefined,
    }
  });

  useEffect(() => {
    if (!clientId || authLoading) {
      if (!authLoading && !user) {
        setLoading(false);
      }
      return;
    }

    const fetchClient = async () => {
      const clientData = await getClient(clientId);
      if (clientData) {
        // Convert stored metric values to display values based on global settings
        const displayHeight = clientData.height ?
          (settings.unit === 'inches' ? Math.round((clientData.height / 2.54) * 10) / 10 : Math.round(clientData.height)) : 0;
        const displayWeight = clientData.weight ?
          (settings.unit === 'inches' ? Math.round((clientData.weight / 0.453592) * 10) / 10 : Math.round(clientData.weight * 10) / 10) : undefined;

        form.reset({
          ...clientData,
          sex: clientData.sex ?? undefined,
          dateOfBirth: safeToDate(clientData.dateOfBirth) ?? undefined,
          height: displayHeight,
          weight: displayWeight,
          liftingExperience: clientData.liftingExperience ?? 0,
          // Phase 6.3 — show the normalized 0–10 NRS value, never the raw stored
          // number (a legacy 0–2 doc would otherwise render as "2/10" instead of
          // its true "8/10 · Severe"). Leave undefined when never recorded —
          // do NOT default to 0 (that's a false "pain-free" clinical claim; see
          // normalizePainLevelNrs's doc comment).
          painLevel: normalizePainLevelNrs(clientData),
        });
      } else {
        toast({
          title: "Error",
          description: "Client not found.",
          variant: "destructive",
        });
        navigate("/clients");
      }
      setLoading(false);
    };

    fetchClient();
  }, [clientId, form, navigate, toast, authLoading, user]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    if (!clientId || !user) {
      if (!user) {
        toast({
          title: "Not Authenticated",
          description: "Please sign in to update the client.",
          variant: "destructive",
        });
      }
      return;
    }

    setIsSubmitting(true);

    // Convert height and weight to metric values (always convert from display units to metric)
    const heightInCM = convertHeightToMetric(values.height, settings.unit);
    const weightInKG = values.weight !== undefined
      ? convertWeightToMetric(values.weight, settings.unit)
      : undefined;

    // Sanitize data: remove undefined values and convert empty strings to null.
    // painLevel is handled separately below (never-recorded must stay absent,
    // not fall through as 0 here — 0 is itself a valid, meaningful NRS value).
    const sanitizedValues = Object.entries(values).reduce((acc, [key, value]) => {
      if (key === 'painLevel') return acc;
      if (value !== undefined && value !== '') {
        acc[key as keyof typeof values] = value;
      }
      return acc;
    }, {} as { [K in keyof typeof values]: any });

    // Replace height and weight with metric values
    const finalValues: any = {
      ...sanitizedValues,
      height: heightInCM,
    };

    // Phase 6.3 — only stamp painLevel + painScale when the practitioner actually set a
    // value on the slider; an untouched/never-recorded value must stay absent
    // rather than persist a false "0/10 pain-free" claim.
    if (values.painLevel !== undefined) {
      finalValues.painLevel = values.painLevel;
      finalValues.painScale = PAIN_SCALE_NRS10;
    }

    // Handle weight: if undefined/empty, send null so the server clears the field
    if (weightInKG === undefined) {
      finalValues.weight = null;
    } else {
      finalValues.weight = weightInKG;
    }

    if (!values.email?.trim()) {
      finalValues.email = null;
    }

    try {
      await updateClient(clientId, finalValues);

      toast({
        title: "Client Updated",
        description: `${values.name}'s details have been successfully updated.`,
      });
      navigate(`/clients/${clientId}`);
    } catch (error) {
      logger.error("Error updating document: ", error);
      toast({
        title: "Error",
        description: "Failed to update client. Please try again.",
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  }

  const backLink = `/clients/${clientId}`;

  if (loading) {
    return (
      <PageLayout
        title="Loading..."
        isLoading={true}
        headerActions={
          <Button variant="ghost" size="sm" asChild>
            <Link to={backLink}>
              <ChevronLeft className="mr-2 h-4 w-4" />
              <span className="hidden sm:inline">Back to Client</span>
            </Link>
          </Button>
        }
      >
        <Card>
          <CardHeader>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-1/2" />
          </CardHeader>
          <CardContent className="space-y-8 pt-6">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </CardContent>
        </Card>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Edit Client"
      subtitle={`Update the details for ${form.getValues("name") || "client"}`}
      headerActions={
        <Button variant="ghost" size="sm" asChild>
          <Link to={backLink}>
            <ChevronLeft className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Back to Client</span>
          </Link>
        </Button>
      }
    >
      <div className="flex flex-col gap-6">
        <ActionToolbar
          className="mb-4 bg-transparent border-none shadow-none p-0"
          endContent={
            <>
              <ToolbarButton
                type="button"
                onClick={() => navigate(backLink)}
              >
                <X className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Cancel</span>
              </ToolbarButton>
              <ToolbarButton
                type="submit"
                form="client-edit-form"
                disabled={isSubmitting}
                isPrimary
              >
                <Save className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{isSubmitting ? "Saving..." : "Save"}</span>
              </ToolbarButton>
            </>
          }
        />
        <Card>
          <CardHeader className="pb-0" />
          <CardContent>
            <Form {...form}>
              <form id="client-edit-form" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 w-full">

                <div className="space-y-8">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-medium text-primary mb-4">Personal Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name <span className="text-error-text">*</span></FormLabel>
                            <FormControl>
                              <Input placeholder="e.g. John Doe" {...field} className="h-12" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="dateOfBirth"
                        render={({ field }) => (
                          <FormItem className="flex flex-col">
                            <FormLabel>Date of Birth</FormLabel>
                            <FormControl>
                              <DatePickerField
                                ref={field.ref}
                                name={field.name}
                                onBlur={field.onBlur}
                                value={field.value}
                                onChange={field.onChange}
                                maxDate={new Date()}
                                minDate={new Date("1900-01-01")}
                                placeholder="Pick a date"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="sex"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Sex</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value ?? ""}>
                              <FormControl>
                                <SelectTrigger className="h-12">
                                  <SelectValue placeholder="Select sex" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {SEX_OPTIONS.map((opt) => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone No.</FormLabel>
                            <FormControl>
                              <Input
                                type="tel"
                                placeholder="e.g. +13108958981"
                                {...field}
                                className="h-12"
                                onBlur={(e) => {
                                  const formatted = formatPhoneInternational(e.target.value);
                                  if (formatted) {
                                    field.onChange(formatted);
                                  }
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="e.g. john.doe@email.com" {...field} className="h-12" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="classification"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Classification</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g. ATP, Swayback" {...field} className="h-12" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Anthropometric Information */}
                  <div>
                    <h3 className="text-lg font-medium text-primary mb-4">Anthropometric Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <FormField
                        control={form.control}
                        name="height"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Height <span className="text-error-text">*</span></FormLabel>
                            <FormControl>
                              <div className="flex items-center gap-2">
                                <Input
                                  name="height"
                                  type="number"
                                  placeholder="e.g. 180"
                                  value={
                                    field.value
                                      ? (typeof field.value === 'number'
                                        ? field.value.toFixed(0)
                                        : field.value)
                                      : ''
                                  }
                                  onChange={e => field.onChange(e.target.value === '' ? undefined : e.target.valueAsNumber)}
                                  className="flex-1 h-12 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                  step="1"
                                  inputMode="decimal"
                                />
                                <div className="text-sm text-muted-foreground min-w-[2rem]">
                                  {settings.unit === 'inches' ? 'in' : 'cm'}
                                </div>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="weight"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Weight</FormLabel>
                            <FormControl>
                              <div className="flex items-center gap-2">
                                <Input
                                  name="weight"
                                  type="number"
                                  placeholder="e.g. 75"
                                  value={
                                    field.value
                                      ? (typeof field.value === 'number'
                                        ? field.value.toFixed(0)
                                        : field.value)
                                      : ''
                                  }
                                  onChange={e => field.onChange(e.target.value === '' ? undefined : e.target.valueAsNumber)}
                                  className="flex-1 h-12 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                  step="1"
                                  inputMode="decimal"
                                />
                                <div className="text-sm text-muted-foreground min-w-[2rem]">
                                  {settings.unit === 'inches' ? 'lbs' : 'kg'}
                                </div>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Address Information */}
                  <div>
                    <h3 className="text-lg font-medium text-primary mb-4">Address Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="address"
                        render={({ field }) => (
                          <FormItem className="col-span-1 md:col-span-2">
                            <FormLabel>Address Line 1</FormLabel>
                            <FormControl>
                              <Input placeholder="e.g. 123 Main St" {...field} className="h-12" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Medical & Goals */}
                  <div>
                    <h3 className="text-lg font-medium text-primary mb-4">Medical & Goals</h3>
                    <div className="grid grid-cols-1 gap-4">
                      <FormField
                        control={form.control}
                        name="goals"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Goals</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="What are the client's primary training goals?"
                                className="min-h-[120px]"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="medicalHistory"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Medical History</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Client's medical history..."
                                  className="min-h-[120px]"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="injuryHistory"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Injury History</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Note any relevant past or current injuries..."
                                  className="min-h-[120px]"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="liftingExperience"
                          render={({ field }) => (
                            <FormItem>
                              <div>
                                <FormLabel>Lifting Experience: <span className="font-semibold text-primary">{experienceLevels[field.value ?? 0]}</span></FormLabel>
                                <FormControl>
                                  <Slider
                                    min={0}
                                    max={3}
                                    step={1}
                                    value={[field.value ?? 0]}
                                    onValueChange={(value) => field.onChange(value[0])}
                                  />
                                </FormControl>
                              </div>
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="painLevel"
                          render={({ field }) => (
                            <PainLevelNrsSliderField value={field.value} onChange={field.onChange} />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
